import React, { useEffect } from "react";
import clsx from "clsx";

import { useTranslation } from "react-i18next";

import { useDropzone, FileError } from "react-dropzone";

import Typography from "@mui/material/Typography";
import { Theme } from "@mui/material/styles";

import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";

import { useInternationalFormatters } from "../shared/helpers/useInternationalFormatters";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "20px",
      borderWidth: 2,
      borderRadius: 2,
      borderColor: "#eeeeee",
      borderStyle: "dashed",
      backgroundColor: "#fafafa",
      color: "#bdbdbd",
      outline: "none",
      transition: "border .24s ease-in-out",
    },
    dragActive: {
      borderColor: theme.palette.info.main,
    },
    dragAccept: {
      borderColor: theme.palette.success.main,
    },
    dragReject: {
      borderColor: theme.palette.error.main,
    },
    successIcon: {
      marginRight: theme.spacing(1),
      color: theme.palette.success.main,
    },
    rejectIcon: {
      marginRight: theme.spacing(1),
      color: theme.palette.error.main,
    },
    fileRule: {
      paddingRight: theme.spacing(1),
      height: "100%",
    },
    fileContainer: {
      marginTop: theme.spacing(1),
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
    fileDetails: {
      display: "flex",
      flexDirection: "row",
      alignItems: "baseline",
    },
    iconContainer: {
      display: "flex",
      flexDirection: "row",
      fontSize: "1.75em",
    },
    fileName: {
      fontSize: 16,
      paddingRight: theme.spacing(2),
      height: "100%",
    },
    fileSize: {
      fontSize: 14,
      height: "100%",
    },
    errorContainer: {
      paddingLeft: theme.spacing(4),
      color: theme.palette.error.main,
    },
  }),
);

interface Props {
  acceptedMimeTypes?: string | string[];
  acceptedFileTypeTranslationKeys?: string | string[];
  maxFiles?: number;
  maxSize?: number;
  onFileSelection: (file: File[]) => void;
}

function getAcceptedFileTypes(acceptedFileTypeTranslationKeys: string | string[] | undefined) {
  if (Array.isArray(acceptedFileTypeTranslationKeys)) {
    return acceptedFileTypeTranslationKeys
      ?.map((translationKey, index) => {
        return `$t(${translationKey}, lowercase)`;
      })
      .join(", ");
  } else return `$t(${acceptedFileTypeTranslationKeys}, lowercase)`;
}

export function DropzoneUploader(props: Props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { bytesToReadableFilesize } = useInternationalFormatters();
  const { onFileSelection } = props;

  const { acceptedFiles, fileRejections, getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } =
    useDropzone({
      accept: props.acceptedMimeTypes,
      multiple: (props.maxFiles && props.maxFiles > 1) || false,
      maxFiles: props.maxFiles,
      maxSize: props.maxSize,
    });

  useEffect(() => {
    onFileSelection(acceptedFiles);
  }, [onFileSelection, acceptedFiles]);

  return (
    <section className="container">
      <div
        {...getRootProps({
          className: clsx(classes.root, {
            [classes.dragActive]: isDragActive,
            [classes.dragAccept]: isDragAccept,
            [classes.dragReject]: isDragReject,
          }),
        })}
      >
        <input {...getInputProps()} />
        <Typography color="textPrimary">{t("fileUpload.selectFile")}</Typography>
        <div className={classes.fileDetails}>
          <Typography color="textPrimary" variant="caption" className={classes.fileRule}>
            {t("fileUpload.acceptedFileTypes")}
          </Typography>
          <Typography color="textPrimary" variant="subtitle2" className={classes.fileRule}>
            {t(getAcceptedFileTypes(props.acceptedFileTypeTranslationKeys))}
          </Typography>
        </div>
        {props.maxSize && (
          <div className={classes.fileDetails}>
            <Typography color="textPrimary" variant="caption" className={classes.fileRule}>
              {t("fileUpload.maximumFileSize")}
            </Typography>
            <Typography color="textPrimary" variant="subtitle2" className={classes.fileRule}>
              {bytesToReadableFilesize(props.maxSize)}
            </Typography>
          </div>
        )}
      </div>

      {acceptedFiles.map((file) => (
        <FileDetails key={`acceptedFile-${file.name}`} file={file} isAccepted />
      ))}
      {fileRejections.map(({ file, errors }) => (
        <FileDetails key={`rejectedFile-${file.name}`} file={file} isAccepted={false} errors={errors} {...props} />
      ))}
    </section>
  );
}

function FileDetails(
  props: {
    file: File;
    isAccepted: boolean;
    errors?: FileError[];
  } & Partial<Props>,
) {
  const { t } = useTranslation();
  const classes = useStyles();

  const { bytesToReadableFilesize } = useInternationalFormatters();

  function getErrorMessage(error: FileError) {
    switch (error.code) {
      case "file-too-large":
        return t("fileUpload.fileTooLarge", {
          val: props.maxSize && bytesToReadableFilesize(props.maxSize),
        });
      case "too-many-files":
        return t("fileUpload.tooManyFiles", { val: props.maxFiles });
      case "file-invalid-type":
        return t("fileUpload.fileInvalidType", {
          val: getAcceptedFileTypes(props.acceptedFileTypeTranslationKeys),
        });
      default:
        return t("fileUpload.fileError");
    }
  }

  return (
    <>
      <div className={classes.fileContainer}>
        <div className={classes.iconContainer}>
          {props.isAccepted ? (
            <FontAwesomeIcon key={`icon-${props.file.name}`} icon={faCheckCircle} className={classes.successIcon} />
          ) : (
            <FontAwesomeIcon
              key={`icon-${props.file.name}`}
              icon={faExclamationCircle}
              className={classes.rejectIcon}
            />
          )}
        </div>
        <div className={classes.fileDetails}>
          <Typography
            color="textPrimary"
            variant="subtitle2"
            component="div"
            align="center"
            className={classes.fileName}
          >
            {props.file.name}
          </Typography>
          <Typography color="textPrimary" variant="body2" className={classes.fileSize}>
            {bytesToReadableFilesize(props.file.size)}
          </Typography>
        </div>
      </div>
      <div className={classes.errorContainer}>
        {props.errors?.map((error) => (
          <li key={`${props.file.name}-${error.code}`}>
            <Typography variant="caption">{getErrorMessage(error)}</Typography>
          </li>
        ))}
      </div>
    </>
  );
}
